import { ActionReducerMap, combineReducers, createFeatureSelector, createSelector } from '@ngrx/store';
import { InjectionToken } from '@angular/core';
import { State } from '@cemex/cmx-client-layout';
import * as fromOrderSusbcriptionState from './order-subscription.reducer';
import * as fromDcoState from '@ordersnproducts/modules/shared/reducers/dco.reducer';
import * as fromSpecialScenariosState from '@ordersnproducts/modules/shared/reducers/special-scenarios.reducer';
import * as fromPoinOfDeliveryState from '@ordersnproducts/modules/shared/reducers/point-of-delivery.reducer';
import { _safe } from '../utils/safe-get.method';
import { getActiveExperienceCode, getFirstCalendar } from '@ordersnproducts/modules/order-builder/reducers';
import { getRmsSpecificationsIsModify } from '@ordersnproducts/modules/order-builder/modules/rms-specification/selectors';
import * as fromReceivingPersons from '@ordersnproducts/modules/shared/modules/receiving-person/redux/reducers/receiving-person.reducer';
import { validateTravelTimeData } from '@ordersnproducts/utils/common-utils';
import * as fromPaymentTerms from '../../shared/modules/payment-terms/redux/payment-terms.reducer';
import * as fromSimulateUL from '../../shared/modules/simulate-ul/redux/simulate-ul.reducer';
import * as fromMapInfo from '@ordersnproducts/modules/shared/reducers/map-info.reducer';

export const featureKey = 'ordersAndProductsComponents';

export interface OrdersAndProductsComponentsFeatureState {
  orderSubscription: fromOrderSusbcriptionState.State;
  dco: fromDcoState.State;
  specialScenarios: fromSpecialScenariosState.SpecialScenariosReducerState;
  pointOfDelivery: fromPoinOfDeliveryState.State;
  receivingPersons: fromReceivingPersons.IReceivingPersonsState;
  paymentTermsState: fromPaymentTerms.IPaymentTermsState;
  simulateULState: fromSimulateUL.ISimulateULState;
  mapInfo: fromMapInfo.MapInfoReducerState;
}

export interface OrdersAndProductsComponentsState extends State {
  OrdersAndProductsComponentsState: OrdersAndProductsComponentsFeatureState;
}

export const reducers = {
  orderSubscription: fromOrderSusbcriptionState.reducer,
  dco: fromDcoState.reducer,
  specialScenarios: fromSpecialScenariosState.specialScenariosReducer,
  pointOfDelivery: fromPoinOfDeliveryState.pointOfDeliveryReducer,
  receivingPersons: fromReceivingPersons.receivingPersonsReducer,
  paymentTermsState: fromPaymentTerms.paymentTermsReducer,
  simulateULState: fromSimulateUL.simulateUlReducer,
  mapInfo: fromMapInfo.reducer,
};

export const combinedReducers = combineReducers({
  ...reducers,
});

export const reducerToken = new InjectionToken<ActionReducerMap<OrdersAndProductsComponentsState>>('COMBINED_REDUCERS');

export function getReducers() {
  return combinedReducers;
}

export const reducerProvider = { provide: reducerToken, useFactory: getReducers };

export const getOrdersAndProductsComponentsState =
  createFeatureSelector<OrdersAndProductsComponentsFeatureState>(featureKey);

// point of delivery

export const getSnapShotPod = createSelector(
  getOrdersAndProductsComponentsState,
  (state) => state.pointOfDelivery && state.pointOfDelivery.snapShot,
);
export const getSelectedPod = createSelector(
  getOrdersAndProductsComponentsState,
  (state) => state.pointOfDelivery && state.pointOfDelivery.selected,
);

// order subscription

export const getOrderSubscriptionState = createSelector(getOrdersAndProductsComponentsState, (state) => {
  return state.orderSubscription;
});

export const getOrderSubscriptionComponentEnabled = createSelector(getOrderSubscriptionState, (state) => {
  return state.componentEnabled;
});

export const getEmails = createSelector(getOrderSubscriptionState, (state) => {
  return state.emails;
});

export const getPhones = createSelector(getOrderSubscriptionState, (state) => {
  return state.phones;
});

export const getOrderSubscriptionDestinations = createSelector(getEmails, getPhones, (emails, phones) => {
  return {
    emails,
    phones,
  };
});

export const getEmailUserSubscriptionToOrder = createSelector(getOrderSubscriptionState, (state) => {
  return state.emailSubscribed;
});

export const getPhoneUserSubscriptionToOrder = createSelector(getOrderSubscriptionState, (state) => {
  return state.phoneSubscribed;
});

export const getUserSubscriptionToOrder = createSelector(
  getEmailUserSubscriptionToOrder,
  getPhoneUserSubscriptionToOrder,
  (emailSubscribed, phoneSubscribed) => {
    return {
      emailSubscribed,
      phoneSubscribed,
    };
  },
);

export const getCanDeleteFirstElement = createSelector(getOrderSubscriptionState, (state) => {
  return state.canDeleteFirstElement;
});

export const getPhoneCode = createSelector(getOrderSubscriptionState, (state) => {
  return state.phoneCode;
});

export const getTriggerSubscriptionToOrder = createSelector(getOrderSubscriptionState, (state) => {
  return state.triggerCheck;
});

export const getShowDiscardModal = createSelector(getOrderSubscriptionState, (state) => {
  return state.showDiscardModal;
});

export const isUserSubcribing = createSelector(getOrderSubscriptionState, (state) => {
  return state.patching;
});

// dco

export const getDcoState = createSelector(getOrdersAndProductsComponentsState, (state) => {
  return state.dco;
});
export const getSpecialScenarioState = createSelector(getOrdersAndProductsComponentsState, (state) => {
  return state.specialScenarios;
});
export const getDcoStatusState = createSelector(getOrdersAndProductsComponentsState, (state) => {
  return state.dco.modifyStatusOrder;
});
export const getDcoColorSpecialScenario = createSelector(getOrdersAndProductsComponentsState, (state) => {
  return state.dco.modifyStatusOrder.colorSpecialScenario;
});

export const getDCOIdHeaderPrefix = createSelector(getDcoState, (state) => {
  return _safe(() => state.entities[0].response.slots[0].header.prefix);
});

export const getSlotForFormIndex = (formIndex: number) =>
  createSelector(getDcoState, (state) => {
    let aux: fromDcoState.Slot;
    for (const id in state.entities) {
      if (Object.prototype.hasOwnProperty.call(state.entities, id)) {
        const element = state.entities[id];
        if (element.formIndex === formIndex) {
          aux = element;
        }
      }
    }
    return aux;
  });

export const getSlotForFormID = (formId: number) =>
  createSelector(getDcoState, (state) => {
    let aux: fromDcoState.Slot;
    for (const id in state.entities) {
      if (Object.prototype.hasOwnProperty.call(state.entities, id)) {
        const element = state.entities[id];
        if (element.formId === formId) {
          aux = element;
        }
      }
    }
    return aux;
  });

export const getDcoEntitiesAsArray = createSelector(getDcoState, (state) => {
  const slots: fromDcoState.Slot[] = [];
  for (const key in state.entities) {
    if (Object.prototype.hasOwnProperty.call(state.entities, key)) {
      slots.push(state.entities[key]);
    }
  }
  return slots;
});
export const getLargeOrderDco = createSelector(getDcoState, (state) => _safe(() => state.largeOrder));

export const getSpecialScenariosAvailable = createSelector(getSpecialScenarioState, (state) =>
  _safe(() => state.specialScenarioActive),
);

export const getDcoIds = createSelector(getDcoState, (state) => _safe(() => state.dcoId));

export const getDCOConcreteProposals = createSelector(getDcoState, (state) =>
  _safe(() => state.entities[0].response.slots[0].concreteProposals),
);

export const getBigOrderQuantity = createSelector(getFirstCalendar, (state) => _safe(() => state.bigOrder.quantity));
export const getSelectedSlot = createSelector(getDcoState, (state) => _safe(() => state.selectedSlot));
export const getSlotAndTravelTime = createSelector(
  getSelectedSlot,
  getDcoState,
  getFirstCalendar,
  getRmsSpecificationsIsModify,
  (slot, dcoState, calendar, isModify) => {
    // only send isScheduleDCO when app is modifying an order
    const resultDCO = { slot, orderLength: _safe(() => dcoState.ids.length), isScheduleDCO: undefined };
    // scenario when app is modifying an order
    if (isModify) {
      // scenario when app didn't get the DCO slot then send order detail's information
      if (!slot || !dcoState) {
        //  send calendar's info to get the slot, this payload would be ignored by the backend with isScheduleDCO = true
        return {
          slot: {
            travelTimeToPlant: _safe(() => calendar.shipment.travelTime),
            travelTimeFromPlant: _safe(() => calendar.shipment.travelTime),
            travelDistance: _safe(() => calendar.shipment.distance, 30),
          },
          orderLength: validateTravelTimeData(calendar.shipment) ? 1 : 0,
          isScheduleDCO: false,
        };
      }
      return { ...resultDCO, isScheduleDCO: true };
    }

    // scenario when app is creating/repeating an order
    // scenario when app didn't get the DCO slot for any reason then send calendar's information
    if (!slot || !dcoState) {
      // send calendar to get the slot
      return {
        ...resultDCO,
        slot: {
          travelTimeToPlant: _safe(() => calendar.shipment.travelTime),
          travelTimeFromPlant: _safe(() => calendar.shipment.travelTime),
          travelDistance: _safe(() => calendar.shipment.distance, 30),
        },
        orderLength: validateTravelTimeData(calendar.shipment) ? 1 : 0,
      };
    }
    // send DCO slot
    return resultDCO;
  },
);

export const getDCOLargeOrder = createSelector(
  // Todo: check if this flag for large order is correct
  getLargeOrderDco,
  getBigOrderQuantity,
  getActiveExperienceCode,
  (largeOrder, bigOrderQuantity) => {
    return {
      largeOrder,
      largeOrderQuantity: _safe(() => bigOrderQuantity),
    };
  },
);

export const getErrorSlotCode = createSelector(getSlotForFormIndex(0), (state) => {
  return _safe(() => state.error.slots[0].requestProductStatusCode);
});

export const getCallDco = createSelector(getDcoState, (state) => _safe(() => state.callDCO));

export const getOutOfServiceState = createSelector(getDcoState, (state) => _safe(() => state.outOfServiceHours));

export const getOutOfServiceCheck = createSelector(getOutOfServiceState, (state) => _safe(() => state.check));

export const getOutOfServiceActive = createSelector(getOutOfServiceState, (state) =>
  _safe(() => state.isOutOfServiceHours),
);

export const getSlotResponseMessage = createSelector(getDcoState, (state) => _safe(() => state.responseMessage));

export const getOpenResetWarningModal = createSelector(getDcoState, (state) => _safe(() => state.openResetWarning));

export const getAcceptedResetWarning = createSelector(getDcoState, (state) => _safe(() => state.acceptedResetWarning));

// MAP INFO SELECTORS
export const getMapInfoState = createSelector(getOrdersAndProductsComponentsState, (state) => {
  return state.mapInfo;
});

export const getMapInfoControlAddress = createSelector(getMapInfoState, (state) => {
  return state.mapInfoVl4?.controlAddress;
});

export const getMapInfoPredictions = createSelector(getMapInfoState, (state) => {
  return state.mapInfoVl4.predictions;
});

export const getMapInfoIsAddress = createSelector(getMapInfoPredictions, (state) => {
  return state.address;
});

export const getMapInfoIsLoading = createSelector(getMapInfoPredictions, (state) => {
  return state.isLoading;
});

export const getPredictionSelected = createSelector(getMapInfoPredictions, (state) => {
  return state.predictionSelect;
});

export const getFormSelected = createSelector(getMapInfoState, (state) => {
  return state.mapInfoVl4.formSelected;
});

export const getMapInfoUpdateValidation = createSelector(getMapInfoState, (state) => {
  return state.mapInfoVl4.updateValidation;
});

export const getIsCreatingPOD = createSelector(getMapInfoState, (state) => {
  return state?.mapInfoVl4?.isCreatingPOD;
});

export const getHasAcceptedResetWarning = createSelector(getMapInfoState, (state) => {
  return state?.mapInfoVl4?.hasAcceptedResetWarning;
});
